import { useState, useEffect } from 'react';
import { templateApi } from '../services/templateApi';
import { tenantApi } from '../services/api';
import { apiClient } from '../services/apiClient';
import TemplateBuilder from '../components/TemplateBuilder';
import LogoPicker from '../components/LogoPicker';
import Handlebars from 'handlebars';
import toast from 'react-hot-toast';
import { Template, TemplateData } from '../types/template';
import { UserData, GraphUser } from '../types/userData';
import { mapUserToTestData, getDummyUserData } from '../utils/userDataMapper';

interface Tenant {
  id: string;
  name: string;
  domain: string;
}

const EXAMPLE_TEMPLATE = `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; font-family: Arial, sans-serif; font-size: 14px;">
  <tr style="line-height: 1.2;">
    <td style="vertical-align: top; padding: 0;">
      <strong>{{displayName}}</strong><br>
      {{jobTitle}}<br>
      {{department}}<br>
      <br>
      <strong>{{companyName}}</strong><br>
      E: <a href="mailto:{{email}}">{{email}}</a><br>
      {{#if mobilePhone}}M: {{mobilePhone}}<br>{{/if}}
    </td>
  </tr>
</table>`;

export default function Templates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [tenantId, setTenantId] = useState<string>('');
  const [tenantName, setTenantName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('dummy');
  const [showForm, setShowForm] = useState(false);
  const [useVisualBuilder, setUseVisualBuilder] = useState(true);
  const [editorMode, setEditorMode] = useState<'visual' | 'html' | 'plaintext'>('visual');
  const [plainTextSignature, setPlainTextSignature] = useState('');
  const [plainTextLogo1, setPlainTextLogo1] = useState('');
  const [plainTextLogo2, setPlainTextLogo2] = useState('');
  const [plainTextLogo1Width, setPlainTextLogo1Width] = useState(120);
  const [plainTextLogo2Width, setPlainTextLogo2Width] = useState(100);
  const [plainTextProfileWidth, setPlainTextProfileWidth] = useState(80);
  const [showLogo1Picker, setShowLogo1Picker] = useState(false);
  const [showLogo2Picker, setShowLogo2Picker] = useState(false);
  const [plainTextFontFamily, setPlainTextFontFamily] = useState('Arial, sans-serif');
  const [plainTextFontSize, setPlainTextFontSize] = useState(14);
  const [plainTextFontColor, setPlainTextFontColor] = useState('#000000');
  const [plainTextLineHeight, setPlainTextLineHeight] = useState(1.6);
  const [selectedTextStart, setSelectedTextStart] = useState(0);
  const [selectedTextEnd, setSelectedTextEnd] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    htmlContent: EXAMPLE_TEMPLATE,
  });

  const [testUserData, setTestUserData] = useState<UserData>(getDummyUserData());
  const [showPreview, setShowPreview] = useState(true);
  const [visualBuilderConfig, setVisualBuilderConfig] = useState<TemplateData | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [builderShowGrid, setBuilderShowGrid] = useState(false);
  const [forceSaveWithoutGrid, setForceSaveWithoutGrid] = useState<boolean | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [cleanHtmlForSaving, setCleanHtmlForSaving] = useState<string | null>(null);
  
  // Custom preview text fields
  const [customPreviewTexts, setCustomPreviewTexts] = useState({
    prefixEnabled: false,
    prefixText: 'Med venlig hilsen',
    suffixEnabled: false,
    suffixText: '',
    disclaimerEnabled: false,
    disclaimerText: 'This email and any attachments are confidential and may be legally privileged.',
    customCompanyEnabled: false,
    customCompanyText: '',
    customAddressEnabled: false,
    customAddressText: '',
  });

  // Render preview with custom text fields
  const renderPreview = () => {
    try {
      // Merge custom overrides with test user data
      const mergedData = { ...testUserData };
      
      if (customPreviewTexts.customCompanyEnabled && customPreviewTexts.customCompanyText) {
        mergedData.companyName = customPreviewTexts.customCompanyText;
      }
      
      // Apply logo URLs from plain text editor
      if (plainTextLogo1) {
        mergedData.logo1 = plainTextLogo1;
      }
      if (plainTextLogo2) {
        mergedData.logo2 = plainTextLogo2;
      }
      
      const template = Handlebars.compile(formData.htmlContent);
      let signatureHtml = template(mergedData);
      
      // Wrap in container for custom texts
      let fullHtml = '';
      
      // Add prefix text (e.g., "Med venlig hilsen")
      if (customPreviewTexts.prefixEnabled && customPreviewTexts.prefixText) {
        const prefixTemplate = Handlebars.compile(customPreviewTexts.prefixText);
        const prefixRendered = prefixTemplate(mergedData);
        fullHtml += `<div style="margin-bottom: 10px;">${prefixRendered.replace(/\n/g, '<br>')}</div>`;
      }
      
      // Add signature
      fullHtml += signatureHtml;
      
      // Add custom address
      if (customPreviewTexts.customAddressEnabled && customPreviewTexts.customAddressText) {
        const addressTemplate = Handlebars.compile(customPreviewTexts.customAddressText);
        const addressRendered = addressTemplate(mergedData);
        fullHtml += `<div style="margin-top: 10px; font-size: 12px; color: #666;">${addressRendered.replace(/\n/g, '<br>')}</div>`;
      }
      
      // Add suffix text
      if (customPreviewTexts.suffixEnabled && customPreviewTexts.suffixText) {
        const suffixTemplate = Handlebars.compile(customPreviewTexts.suffixText);
        const suffixRendered = suffixTemplate(mergedData);
        fullHtml += `<div style="margin-top: 10px;">${suffixRendered.replace(/\n/g, '<br>')}</div>`;
      }
      
      // Add disclaimer
      if (customPreviewTexts.disclaimerEnabled && customPreviewTexts.disclaimerText) {
        const disclaimerTemplate = Handlebars.compile(customPreviewTexts.disclaimerText);
        const disclaimerRendered = disclaimerTemplate(mergedData);
        fullHtml += `<div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #e0e0e0; font-size: 11px; color: #999;">${disclaimerRendered.replace(/\n/g, '<br>')}</div>`;
      }
      
      return fullHtml;
    } catch (error) {
      return `<p style="color: red;">Error rendering template: ${error}</p>`;
    }
  };

  // Register Handlebars helper once on mount
  useEffect(() => {
    // Check if helper already exists to avoid re-registration
    if (!Handlebars.helpers['if']) {
      Handlebars.registerHelper('if', function(this: any, conditional: any, options: any) {
        if (conditional) {
          return options.fn(this);
        } else {
          return options.inverse(this);
        }
      });
    }
  }, []);

  useEffect(() => {
    // Get current user's tenant on mount
    apiClient.get('/api/auth/me')
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
        }
      })
      .catch((error) => console.error('Failed to get user info:', error));
  }, []);

  useEffect(() => {
    if (tenantId) {
      loadTemplates();
      loadUsers();
    }
  }, [tenantId]);

  useEffect(() => {
    if (selectedUserId === 'dummy') {
      setTestUserData(getDummyUserData());
    } else if (selectedUserId && users.length > 0) {
      const user = users.find(u => u.id === selectedUserId) as GraphUser | undefined;
      if (user) {
        const userName = user.displayName || 'User';
        const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName.split(' - ')[0])}&size=80&background=0066cc&color=fff`;
        
        // Fetch profile photo and map user data
        apiClient.get(`/api/users/photo/${user.id}`)
          .then((photoData) => {
            const profilePicUrl = photoData.success && photoData.data.photoUrl 
              ? photoData.data.photoUrl 
              : fallbackUrl;
            setTestUserData(mapUserToTestData(user, tenantName, profilePicUrl));
          })
          .catch((error) => {
            console.error('Failed to fetch profile photo:', error);
            setTestUserData(mapUserToTestData(user, tenantName, fallbackUrl));
          });
      }
    }
  }, [selectedUserId, users, tenantId, tenantName]);

  // When grid is disabled for saving, capture clean HTML and continue
  useEffect(() => {
    if (forceSaveWithoutGrid === false && formData.htmlContent) {
      // Clean HTML is ready, trigger save by marking it
      setCleanHtmlForSaving(formData.htmlContent);
    }
  }, [forceSaveWithoutGrid, formData.htmlContent]);

  // When clean HTML is captured, proceed with actual save
  useEffect(() => {
    if (cleanHtmlForSaving) {
      // Continue with save process
      const proceedWithSave = async () => {
        try {
          setIsSaving(true);
          
          console.log('[Templates] Saving template with HTML length:', cleanHtmlForSaving.length);
          console.log('[Templates] HTML preview (first 500 chars):', cleanHtmlForSaving.substring(0, 500));
          
          // Prepare variables object
          let variables = visualBuilderConfig;
          
          if (editorMode === 'plaintext') {
            variables = {
              ...variables,
              plainTextSettings: {
                logo1: plainTextLogo1,
                logo2: plainTextLogo2,
                logo1Width: plainTextLogo1Width,
                logo2Width: plainTextLogo2Width,
                profileWidth: plainTextProfileWidth,
                fontFamily: plainTextFontFamily,
                fontSize: plainTextFontSize,
                fontColor: plainTextFontColor,
                lineHeight: plainTextLineHeight,
              },
            };
          }
          
          variables = {
            ...variables,
            customPreviewTexts,
          };
          
          const templateData = {
            ...formData,
            htmlContent: cleanHtmlForSaving, // Use the clean HTML
            plainTextContent: editorMode === 'plaintext' ? plainTextSignature : (formData as any).plainTextContent || null,
            variables,
          };
          
          if (editingId) {
            await templateApi.update(editingId, templateData);
            toast.success('Template updated successfully');
          } else {
            await templateApi.create(tenantId, templateData);
            toast.success('Template created successfully');
          }
          
          loadTemplates();
          
          // Restore grid visualization
          setForceSaveWithoutGrid(undefined);
          
          // Keep modal visible for 2 seconds
          await new Promise(resolve => setTimeout(resolve, 2000));
          setIsSaving(false);
          
          // Clear the clean HTML marker
          setCleanHtmlForSaving(null);
        } catch (error) {
          console.error('Failed to save template:', error);
          toast.error('Failed to save template');
          setForceSaveWithoutGrid(undefined);
          setIsSaving(false);
          setCleanHtmlForSaving(null);
        }
      };
      
      proceedWithSave();
    }
  }, [cleanHtmlForSaving]);

  const loadTemplates = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      const response = await templateApi.getAll(tenantId);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!tenantId) return;
    try {
      const data = await apiClient.get(`/api/users/${tenantId}?limit=100`);
      if (data.success && data.data.length > 0) {
        setUsers(data.data);
        // Get tenant name from first user if available
        if (data.data[0]?.tenant?.name) {
          setTenantName(data.data[0].tenant.name);
        }
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) {
      toast.error('Please wait for tenant info to load');
      return;
    }

    // Step 1: Check if grid needs to be disabled
    const wasShowingGrid = builderShowGrid;
    if (wasShowingGrid) {
      // Disable grid and wait for clean HTML
      setForceSaveWithoutGrid(false);
      return; // Exit - will continue in useEffect when clean HTML is ready
    }
    
    // Step 2: Grid is already disabled (or wasn't enabled), proceed with save
    try {
      // Show saving modal
      setIsSaving(true);
      
      console.log('[Templates] Saving template with HTML length:', formData.htmlContent.length);
      console.log('[Templates] HTML preview (first 500 chars):', formData.htmlContent.substring(0, 500));
      
      // Prepare variables object based on editor mode
      let variables = visualBuilderConfig;
      
      // If in plaintext mode, save logos and styling settings
      if (editorMode === 'plaintext') {
        variables = {
          ...variables,
          plainTextSettings: {
            logo1: plainTextLogo1,
            logo2: plainTextLogo2,
            logo1Width: plainTextLogo1Width,
            logo2Width: plainTextLogo2Width,
            profileWidth: plainTextProfileWidth,
            fontFamily: plainTextFontFamily,
            fontSize: plainTextFontSize,
            fontColor: plainTextFontColor,
            lineHeight: plainTextLineHeight,
          },
        };
      }
      
      // Always save custom preview texts
      variables = {
        ...variables,
        customPreviewTexts,
      };
      
      const templateData = {
        ...formData,
        plainTextContent: editorMode === 'plaintext' ? plainTextSignature : (formData as any).plainTextContent || null,
        variables, // Save Visual Builder config, plaintext logos, and custom preview texts
      };
      
      if (editingId) {
        await templateApi.update(editingId, templateData);
        toast.success('Template updated successfully');
      } else {
        await templateApi.create(tenantId, templateData);
        toast.success('Template created successfully');
      }
      // Don't close the form - let user continue editing
      loadTemplates();
      
      // Restore grid visualization if it was enabled
      if (wasShowingGrid) {
        setForceSaveWithoutGrid(undefined);
      }
      
      // Keep saving modal visible for 2 seconds for visual feedback
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsSaving(false);
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
      
      // Restore grid visualization even if save failed
      if (wasShowingGrid) {
        setForceSaveWithoutGrid(undefined);
      }
      
      // Hide saving modal on error
      setIsSaving(false);
    }
  };

  const handleEdit = (template: Template) => {
    console.log('Editing template:', template);
    console.log('HTML content length:', template.htmlContent?.length);
    
    // Close form first to ensure clean state
    setShowForm(false);
    
    // Use setTimeout to ensure state updates in next tick
    setTimeout(() => {
      setEditingId(template.id);
      setVisualBuilderConfig(template.variables || null); // Load visual builder config if available
      
      // Load custom preview texts if saved
      if (template.variables?.customPreviewTexts) {
        setCustomPreviewTexts(template.variables.customPreviewTexts);
      } else {
        // Reset to defaults
        setCustomPreviewTexts({
          prefixEnabled: false,
          prefixText: 'Med venlig hilsen',
          suffixEnabled: false,
          suffixText: '',
          disclaimerEnabled: false,
          disclaimerText: 'This email and any attachments are confidential and may be legally privileged.',
          customCompanyEnabled: false,
          customCompanyText: '',
          customAddressEnabled: false,
          customAddressText: '',
        });
      }
      
      // Load plaintext settings from saved template
      if (template.variables?.plainTextSettings) {
        const settings = template.variables.plainTextSettings;
        setPlainTextLogo1(settings.logo1 || '');
        setPlainTextLogo2(settings.logo2 || '');
        setPlainTextLogo1Width(settings.logo1Width || 120);
        setPlainTextLogo2Width(settings.logo2Width || 100);
        setPlainTextProfileWidth(settings.profileWidth || 80);
        setPlainTextFontFamily(settings.fontFamily || 'Arial, sans-serif');
        setPlainTextFontSize(settings.fontSize || 14);
        setPlainTextFontColor(settings.fontColor || '#000000');
        setPlainTextLineHeight(settings.lineHeight || 1.6);
      } else if (template.variables?.plainTextLogos) {
        // Backward compatibility with old format
        setPlainTextLogo1(template.variables.plainTextLogos.logo1 || '');
        setPlainTextLogo2(template.variables.plainTextLogos.logo2 || '');
        setPlainTextLogo1Width(120);
        setPlainTextLogo2Width(100);
        setPlainTextProfileWidth(80);
        setPlainTextFontFamily('Arial, sans-serif');
        setPlainTextFontSize(14);
        setPlainTextFontColor('#000000');
        setPlainTextLineHeight(1.6);
      } else {
        // Reset to defaults
        setPlainTextLogo1('');
        setPlainTextLogo2('');
        setPlainTextLogo1Width(120);
        setPlainTextLogo2Width(100);
        setPlainTextProfileWidth(80);
        setPlainTextFontFamily('Arial, sans-serif');
        setPlainTextFontSize(14);
        setPlainTextFontColor('#000000');
        setPlainTextLineHeight(1.6);
      }
      
      setFormData({
        name: template.name,
        description: template.description || '',
        htmlContent: template.htmlContent,
      });
      
      // Load saved plain text content if available
      if (template.plainTextContent) {
        setPlainTextSignature(template.plainTextContent);
      } else if (template.htmlContent) {
        // Fallback: convert HTML back to plain text for plain text editor
        const plainText = template.htmlContent
          // Remove the table wrapper
          .replace(/<table[^>]*>/gi, '')
          .replace(/<\/table>/gi, '')
          .replace(/<tr[^>]*>/gi, '')
          .replace(/<\/tr>/gi, '')
          .replace(/<td[^>]*>/gi, '')
          .replace(/<\/td>/gi, '')
          // Convert profile picture HTML back to variable
          .replace(/\{\{#if profilePicture\}\}<img[^>]*>\{\{\/if\}\}/gi, '{{profilePicture}}')
          // Convert logo HTML back to variables
          .replace(/\{\{#if logo1\}\}<img[^>]*>\{\{\/if\}\}/gi, '{{logo1}}')
          .replace(/\{\{#if logo2\}\}<img[^>]*>\{\{\/if\}\}/gi, '{{logo2}}')
          // Convert <br> to newlines
          .replace(/<br\s*\/?>/gi, '\n')
          // Remove any remaining HTML tags
          .replace(/<[^>]*>/g, '')
          // Decode HTML entities
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"');
        setPlainTextSignature(plainText.trim());
      }
      
      setShowForm(true);
    }, 0);
  };

  const handleCopy = async (template: Template) => {
    try {
      const copyName = `${template.name} (Copy)`;
      await templateApi.create(tenantId, {
        name: copyName,
        description: template.description || '',
        htmlContent: template.htmlContent,
        plainTextContent: template.plainTextContent || null,
        variables: template.variables,
      });
      loadTemplates();
    } catch (error) {
      console.error('Failed to copy template:', error);
      toast.error('Failed to copy template');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await templateApi.delete(id);
      setDeleteConfirm(null);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const insertVariable = (variable: string) => {
    setFormData({
      ...formData,
      htmlContent: formData.htmlContent + `{{${variable}}}`,
    });
  };

  // Helper function to apply formatting to selected text
  const applyFormatting = (formatType: 'bold' | 'italic' | 'underline' | 'color' | 'size' | 'font', value?: string) => {
    const textarea = document.getElementById('plainTextEditor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = plainTextSignature;
    const selectedText = text.substring(start, end);
    
    if (!selectedText) {
      alert('Please select some text first');
      return;
    }
    
    let wrappedText = '';
    
    switch (formatType) {
      case 'bold':
        wrappedText = `<b>${selectedText}</b>`;
        break;
      case 'italic':
        wrappedText = `<i>${selectedText}</i>`;
        break;
      case 'underline':
        wrappedText = `<u>${selectedText}</u>`;
        break;
      case 'color':
        wrappedText = `<span style="color: ${value};">${selectedText}</span>`;
        break;
      case 'size':
        wrappedText = `<span style="font-size: ${value}px;">${selectedText}</span>`;
        break;
      case 'font':
        wrappedText = `<span style="font-family: ${value};">${selectedText}</span>`;
        break;
    }
    
    const newText = text.substring(0, start) + wrappedText + text.substring(end);
    setPlainTextSignature(newText);
    const htmlContent = convertPlainTextToHtml(newText);
    setFormData({ ...formData, htmlContent });
    
    // Restore focus and cursor position
    setTimeout(() => {
      textarea.focus();
      const newPos = start + wrappedText.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  // Helper function to convert plain text signature to HTML with styling
  const convertPlainTextToHtml = (text: string) => {
    let htmlContent = text
      .replace(/{{profilePicture}}/g, `{{#if profilePicture}}<img src="{{profilePicture}}" alt="Profile" width="${plainTextProfileWidth}" height="${plainTextProfileWidth}" style="border-radius: 50%; display: inline-block; margin-right: 10px; vertical-align: middle;">{{/if}}`)
      .replace(/{{logo1}}/g, `{{#if logo1}}<img src="{{logo1}}" alt="Logo" width="${plainTextLogo1Width}" style="display: inline-block; margin-right: 10px; vertical-align: middle;">{{/if}}`)
      .replace(/{{logo2}}/g, `{{#if logo2}}<img src="{{logo2}}" alt="Secondary Logo" width="${plainTextLogo2Width}" style="display: inline-block; margin-right: 10px; vertical-align: middle;">{{/if}}`)
      // Only apply markdown if not already HTML tagged (avoid double-wrapping)
      .replace(/\*\*(?![^<]*>)(.+?)\*\*/g, '<strong>$1</strong>') // Bold: **text**
      .replace(/\*(?![^<]*>)(.+?)\*/g, '<em>$1</em>') // Italic: *text*
      .replace(/_(?![^<]*>)(.+?)_/g, '<u>$1</u>') // Underline: _text_
      .replace(/  /g, ' &nbsp;') // Preserve multiple spaces
      .replace(/\n/g, '<br>');
    htmlContent = `<table style="font-family: ${plainTextFontFamily}; font-size: ${plainTextFontSize}px; color: ${plainTextFontColor}; line-height: ${plainTextLineHeight};"><tr><td style="white-space: pre-wrap;">${htmlContent}</td></tr></table>`;
    return htmlContent;
  };

  const variables = [
    'profilePicture', 'logo1', 'logo2', 'displayName', 'givenName', 'surname', 'email', 'jobTitle',
    'department', 'officeLocation', 'streetAddress', 'city', 'state', 'postalCode',
    'country', 'companyName', 'companyDomain', 'mobilePhone', 'businessPhones',
    'faxNumber', 'employeeId', 'employeeType', 'employeeHireDate',
    'preferredLanguage', 'usageLocation',
    'extensionAttribute1', 'extensionAttribute2', 'extensionAttribute3',
    'extensionAttribute4', 'extensionAttribute5', 'extensionAttribute6',
    'extensionAttribute7', 'extensionAttribute8', 'extensionAttribute9',
    'extensionAttribute10', 'extensionAttribute11', 'extensionAttribute12',
    'extensionAttribute13', 'extensionAttribute14', 'extensionAttribute15',
    'vatNumber', 'address'
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Delete Template</h3>
            <p style={{ marginBottom: '24px', color: '#666' }}>
              Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>? This will remove all assignments and deployments.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    <div style={{ display: 'grid', gridTemplateColumns: showForm ? '400px 1fr' : '1fr', gap: '20px', height: 'calc(100vh - 100px)' }}>
      {/* Left Column: Template List */}
      <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', position: 'sticky', top: 0, backgroundColor: 'white', paddingBottom: '10px', zIndex: 10 }}>
          <h2 style={{ margin: 0 }}>Templates ({templates.length})</h2>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) {
                setEditingId(null);
                setFormData({ name: '', description: '', htmlContent: EXAMPLE_TEMPLATE });
                setVisualBuilderConfig(null);
                setPlainTextLogo1('');
                setPlainTextLogo2('');
                setPlainTextSignature('');
                setPlainTextLogo1Width(120);
                setPlainTextLogo2Width(100);
                setPlainTextProfileWidth(80);
                setPlainTextFontFamily('Arial, sans-serif');
                setPlainTextFontSize(14);
                setPlainTextFontColor('#000000');
                setPlainTextLineHeight(1.6);
              }
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: showForm ? '#6c757d' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
            disabled={!tenantId}
          >
            {showForm ? '✕ Close' : '+ New'}
          </button>
        </div>

        {/* Template List */}
        {loading ? (
          <p>Loading...</p>
        ) : templates.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666', border: '2px dashed #ddd', borderRadius: '8px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📝</div>
            <p>No templates yet</p>
            <p style={{ fontSize: '14px' }}>Click "+ New" to create your first template</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {templates.map((template) => (
              <div
                key={template.id}
                onClick={() => handleEdit(template)}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: editingId === template.id ? '2px solid #0066cc' : '2px solid #e5e7eb',
                  background: editingId === template.id ? '#f0f8ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={(e) => {
                  if (editingId !== template.id) {
                    e.currentTarget.style.borderColor = '#c7d2fe';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (editingId !== template.id) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {editingId === template.id && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#0066cc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}>
                    ✓
                  </div>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '15px', marginBottom: '6px' }}>
                    {template.name}
                  </div>
                  {template.description && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', lineHeight: '1.4' }}>
                      {template.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '500',
                      backgroundColor: template.isActive ? '#d1fae5' : '#fee2e2',
                      color: template.isActive ? '#059669' : '#dc2626',
                    }}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {new Date(template.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div style={{ marginTop: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(template);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '60px',
                      padding: '6px 12px',
                      backgroundColor: '#0066cc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(template);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '60px',
                      padding: '6px 12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                  >
                    Copy
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm({ id: template.id, name: template.name });
                    }}
                    style={{
                      flex: 1,
                      minWidth: '60px',
                      padding: '6px 12px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Column: Editor */}
      {showForm && (
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          overflowY: 'auto',
          padding: '20px',
        }}>
          <h3>{editingId ? 'Edit Template' : 'Create New Template'}</h3>
          
          <div style={{ marginBottom: '15px' }}>
            <button
              type="button"
              onClick={() => { setEditorMode('visual'); setUseVisualBuilder(true); }}
              style={{
                padding: '8px 16px',
                backgroundColor: editorMode === 'visual' ? '#0066cc' : '#e0e0e0',
                color: editorMode === 'visual' ? 'white' : '#000',
                border: 'none',
                borderRadius: '4px 0 0 4px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
              }}
            >
              Visual Builder
            </button>
            <button
              type="button"
              onClick={() => { setEditorMode('plaintext'); setUseVisualBuilder(false); }}
              style={{
                padding: '8px 16px',
                backgroundColor: editorMode === 'plaintext' ? '#0066cc' : '#e0e0e0',
                color: editorMode === 'plaintext' ? 'white' : '#000',
                border: 'none',
                borderRadius: '0',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                borderLeft: '1px solid #ccc',
              }}
            >
              Plain Text
            </button>
            <button
              type="button"
              onClick={() => { setEditorMode('html'); setUseVisualBuilder(false); }}
              style={{
                padding: '8px 16px',
                backgroundColor: editorMode === 'html' ? '#0066cc' : '#e0e0e0',
                color: editorMode === 'html' ? 'white' : '#000',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                borderLeft: '1px solid #ccc',
              }}
            >
              &lt;/&gt; HTML Editor
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div style={{ flex: 1, marginRight: '15px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Template Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Standard Corporate Signature"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Description</label>
                  <input
                    type="text"
                    placeholder="Optional description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>
              </div>
              
              <button
                type="submit"
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#0066cc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                {editingId ? 'Update Template' : 'Create Template'}
              </button>
            </div>

            {editorMode === 'plaintext' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Plain Text Signature</label>
                  <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px' }}>
                    Used when Outlook composes in plain text mode. Use {'{{variables}}'} — press Enter for line breaks.
                  </p>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ fontSize: '12px' }}>Variables:</strong>{' '}
                    {variables.filter(v => !v.startsWith('logo') && v !== 'profilePicture').map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById('plainTextContentEditor') as HTMLTextAreaElement;
                          if (textarea) {
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const current = plainTextSignature;
                            const newText = current.substring(0, start) + `{{${v}}}` + current.substring(end);
                            setPlainTextSignature(newText);
                            setTimeout(() => {
                              textarea.focus();
                              const newPos = start + v.length + 4;
                              textarea.setSelectionRange(newPos, newPos);
                            }, 0);
                          } else {
                            setPlainTextSignature(plainTextSignature + `{{${v}}}`);
                          }
                        }}
                        style={{
                          padding: '3px 6px',
                          margin: '2px',
                          fontSize: '10px',
                          backgroundColor: '#e0e0e0',
                          border: '1px solid #999',
                          borderRadius: '3px',
                          cursor: 'pointer',
                        }}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  <textarea
                    id="plainTextContentEditor"
                    value={plainTextSignature}
                    onChange={(e) => setPlainTextSignature(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      minHeight: '300px',
                      border: '2px solid #d1d5db',
                      borderRadius: '6px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre',
                    }}
                    placeholder={`{{givenName}} {{surname}}\n{{jobTitle}}\n{{companyName}}\n\nE: {{email}}\nM: {{mobilePhone}}\nW: https://{{companyDomain}}`}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '6px' }}>
                    Line breaks are preserved as-is. No HTML — pure text for plain text emails.
                  </small>
                </div>
                <div style={{ position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontWeight: 'bold' }}>Live Preview</label>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Preview as:</label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value="dummy">Dummy Test User</option>
                      {users.length > 0 && <option disabled>────────────────</option>}
                      {users.map((user) => {
                        const email = user.mail || user.userPrincipalName;
                        return (
                          <option key={user.id} value={user.id}>
                            {user.displayName}{email ? ` (${email})` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <small style={{ color: '#666', fontSize: '11px' }}>
                      {selectedUserId === 'dummy' ? 'Using dummy test data' : 'Previewing with real user data'}
                    </small>
                  </div>
                  <div style={{
                    border: '2px solid #0066cc',
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    minHeight: '200px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {(() => {
                      try {
                        if (!plainTextSignature) return <span style={{ color: '#999' }}>Start typing to see preview...</span>;
                        const template = Handlebars.compile(plainTextSignature);
                        return template(testUserData);
                      } catch (e: any) {
                        return <span style={{ color: 'red' }}>Error: {e.message}</span>;
                      }
                    })()}
                  </div>
                </div>
              </div>
            ) : editorMode === 'visual' ? (
              <>
                {/* TOP: Field List and Preview side-by-side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <TemplateBuilder
                      key={editingId || 'new'}
                      initialData={visualBuilderConfig}
                      onGenerate={(html) => setFormData(prev => ({ ...prev, htmlContent: html }))}
                      onDataChange={(config) => setVisualBuilderConfig(config)}
                      forceShowGrid={forceSaveWithoutGrid}
                      onShowGridChange={(showGrid) => setBuilderShowGrid(showGrid)}
                    />
                  </div>
                  <div key={`preview-${editingId || 'new'}`} style={{ position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ fontWeight: 'bold' }}>Live Preview</label>
                    </div>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Preview as:</label>
                        <select
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            color: '#1f2937',
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            appearance: 'none',
                            backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 10px center',
                            backgroundSize: '20px',
                            paddingRight: '40px',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#0066cc';
                            e.target.style.boxShadow = '0 0 0 3px rgba(0,102,204,0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                          }}
                        >
                          <option value="dummy">Dummy Test User</option>
                          {users.length > 0 && <option disabled>────────────────</option>}
                          {users.map((user) => {
                            const email = user.mail || user.userPrincipalName;
                            return (
                              <option key={user.id} value={user.id}>
                                {user.displayName}{email ? ` (${email})` : ''}
                              </option>
                            );
                          })}
                        </select>
                        <small style={{ color: '#666', fontSize: '11px' }}>
                          {selectedUserId === 'dummy' ? 'Using dummy test data' : 'Previewing with real user data from Microsoft 365'}
                        </small>
                      </div>
                      
                      <div style={{
                        border: '2px solid #0066cc',
                        padding: '15px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        minHeight: '200px',
                        overflow: 'auto',
                      }}>
                        <div key={formData.htmlContent + JSON.stringify(visualBuilderConfig)} dangerouslySetInnerHTML={{ __html: renderPreview() }} />
                      </div>
                      
                      <details style={{ marginTop: '10px' }}>
                        <summary style={{ cursor: 'pointer', color: '#0066cc', fontSize: '13px' }}>Edit Test User Data</summary>
                        <div style={{ marginTop: '10px', fontSize: '12px' }}>
                          {Object.entries(testUserData).map(([key, value]) => (
                            <div key={key} style={{ marginBottom: '8px' }}>
                              <label style={{ display: 'inline-block', width: '150px', fontWeight: '500' }}>{key}:</label>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => setTestUserData({ ...testUserData, [key]: e.target.value })}
                                style={{ padding: '4px', fontSize: '12px', width: '250px' }}
                              />
                            </div>
                          ))}
                        </div>
                      </details>
                      
                      {/* Custom Preview Text Fields */}
                      <details style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                        <summary style={{ cursor: 'pointer', color: '#0066cc', fontSize: '13px', fontWeight: '600' }}>Custom Preview Texts</summary>
                        <div style={{ marginTop: '10px', fontSize: '12px' }}>
                          {/* Prefix Text */}
                          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                              <input
                                type="checkbox"
                                checked={customPreviewTexts.prefixEnabled}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, prefixEnabled: e.target.checked })}
                                style={{ marginRight: '6px' }}
                              />
                              Prefix Text (e.g., greeting)
                            </label>
                            {customPreviewTexts.prefixEnabled && (
                              <textarea
                                value={customPreviewTexts.prefixText}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, prefixText: e.target.value })}
                                placeholder="Med venlig hilsen"
                                style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '50px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            )}
                            <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Appears before signature. Supports {'{{variables}}'}</small>
                          </div>
                          
                          {/* Custom Company Override */}
                          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                              <input
                                type="checkbox"
                                checked={customPreviewTexts.customCompanyEnabled}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customCompanyEnabled: e.target.checked })}
                                style={{ marginRight: '6px' }}
                              />
                              Override Company Name
                            </label>
                            {customPreviewTexts.customCompanyEnabled && (
                              <input
                                type="text"
                                value={customPreviewTexts.customCompanyText}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customCompanyText: e.target.value })}
                                placeholder="Alternative Company Name"
                                style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            )}
                            <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Test with different company names</small>
                          </div>
                          
                          {/* Custom Address */}
                          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                              <input
                                type="checkbox"
                                checked={customPreviewTexts.customAddressEnabled}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customAddressEnabled: e.target.checked })}
                                style={{ marginRight: '6px' }}
                              />
                              Custom Address/Footer
                            </label>
                            {customPreviewTexts.customAddressEnabled && (
                              <textarea
                                value={customPreviewTexts.customAddressText}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customAddressText: e.target.value })}
                                placeholder="Virtue ApS - Krogholmgårdsvej 79 2950 - cvr: 35875123"
                                style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '60px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            )}
                            <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Additional address or footer info. Supports {'{{variables}}'}</small>
                          </div>
                          
                          {/* Suffix Text */}
                          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                              <input
                                type="checkbox"
                                checked={customPreviewTexts.suffixEnabled}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, suffixEnabled: e.target.checked })}
                                style={{ marginRight: '6px' }}
                              />
                              Suffix Text (e.g., additional info)
                            </label>
                            {customPreviewTexts.suffixEnabled && (
                              <textarea
                                value={customPreviewTexts.suffixText}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, suffixText: e.target.value })}
                                placeholder="Additional information..."
                                style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '50px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            )}
                            <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Appears after signature. Supports {'{{variables}}'}</small>
                          </div>
                          
                          {/* Disclaimer */}
                          <div style={{ marginBottom: '6px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                              <input
                                type="checkbox"
                                checked={customPreviewTexts.disclaimerEnabled}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, disclaimerEnabled: e.target.checked })}
                                style={{ marginRight: '6px' }}
                              />
                              Disclaimer Text
                            </label>
                            {customPreviewTexts.disclaimerEnabled && (
                              <textarea
                                value={customPreviewTexts.disclaimerText}
                                onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, disclaimerText: e.target.value })}
                                placeholder="Legal disclaimer or confidentiality notice..."
                                style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '60px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            )}
                            <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Small text at bottom. Supports {'{{variables}}'}</small>
                          </div>
                        </div>
                      </details>
                  </div>
                </div>
                <small style={{ color: '#666', display: 'block', marginTop: '10px' }}>
                  The visual builder creates new templates. To edit existing HTML, switch to HTML Editor mode.
                </small>
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px' }}>HTML Content *</label>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Variables:</strong> {variables.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => insertVariable(v)}
                          style={{
                            padding: '4px 8px',
                            margin: '2px',
                            fontSize: '11px',
                            backgroundColor: '#e0e0e0',
                            border: '1px solid #999',
                            borderRadius: '3px',
                            cursor: 'pointer',
                          }}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                    <textarea
                      required
                      value={formData.htmlContent}
                      onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        minHeight: '300px',
                      }}
                      placeholder="Enter HTML signature template with {{variables}}"
                    />
                    <small style={{ color: '#666' }}>
                      Use Handlebars syntax: {`{{variable}}`} or {`{{#if variable}}...{{/if}}`}
                    </small>
                  </div>
                </div>

                <div key={`preview-${editingId || 'new'}`} style={{ position: 'sticky', top: '20px', alignSelf: 'flex-start' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontWeight: 'bold' }}>Live Preview</label>
                  </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>Preview as:</label>
                      <select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          fontSize: '14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          color: '#1f2937',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none',
                          backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236b7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 10px center',
                          backgroundSize: '20px',
                          paddingRight: '40px',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0066cc';
                          e.target.style.boxShadow = '0 0 0 3px rgba(0,102,204,0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                        }}
                      >
                        <option value="dummy">Dummy Test User</option>
                        {users.length > 0 && <option disabled>────────────────</option>}
                        {users.map((user) => {
                          const email = user.mail || user.userPrincipalName;
                          return (
                            <option key={user.id} value={user.id}>
                              {user.displayName}{email ? ` (${email})` : ''}
                            </option>
                          );
                        })}
                      </select>
                      <small style={{ color: '#666', fontSize: '11px' }}>
                        {selectedUserId === 'dummy' ? 'Using dummy test data' : 'Previewing with real user data from Microsoft 365'}
                      </small>
                    </div>
                    
                    <div style={{
                      border: '2px solid #0066cc',
                      padding: '15px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      minHeight: '200px',
                      overflow: 'auto',
                    }}>
                      <div dangerouslySetInnerHTML={{ __html: renderPreview() }} />
                    </div>
                    
                    <details style={{ marginTop: '10px' }}>
                      <summary style={{ cursor: 'pointer', color: '#0066cc', fontSize: '13px' }}>Edit Test User Data</summary>
                      <div style={{ marginTop: '10px', fontSize: '12px' }}>
                        {Object.entries(testUserData).map(([key, value]) => (
                          <div key={key} style={{ marginBottom: '8px' }}>
                            <label style={{ display: 'inline-block', width: '150px', fontWeight: '500' }}>{key}:</label>
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => setTestUserData({ ...testUserData, [key]: e.target.value })}
                              style={{ padding: '4px', fontSize: '12px', width: '250px' }}
                            />
                          </div>
                        ))}
                      </div>
                    </details>
                    
                    {/* Custom Preview Text Fields */}
                    <details style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                      <summary style={{ cursor: 'pointer', color: '#0066cc', fontSize: '13px', fontWeight: '600' }}>Custom Preview Texts</summary>
                      <div style={{ marginTop: '10px', fontSize: '12px' }}>
                        {/* Prefix Text */}
                        <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                            <input
                              type="checkbox"
                              checked={customPreviewTexts.prefixEnabled}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, prefixEnabled: e.target.checked })}
                              style={{ marginRight: '6px' }}
                            />
                            Prefix Text (e.g., greeting)
                          </label>
                          {customPreviewTexts.prefixEnabled && (
                            <textarea
                              value={customPreviewTexts.prefixText}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, prefixText: e.target.value })}
                              placeholder="Med venlig hilsen"
                              style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '50px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          )}
                          <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Appears before signature. Supports {'{{variables}}'}</small>
                        </div>
                        
                        {/* Custom Company Override */}
                        <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                            <input
                              type="checkbox"
                              checked={customPreviewTexts.customCompanyEnabled}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customCompanyEnabled: e.target.checked })}
                              style={{ marginRight: '6px' }}
                            />
                            Override Company Name
                          </label>
                          {customPreviewTexts.customCompanyEnabled && (
                            <input
                              type="text"
                              value={customPreviewTexts.customCompanyText}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customCompanyText: e.target.value })}
                              placeholder="Alternative Company Name"
                              style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          )}
                          <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Test with different company names</small>
                        </div>
                        
                        {/* Custom Address */}
                        <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                            <input
                              type="checkbox"
                              checked={customPreviewTexts.customAddressEnabled}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customAddressEnabled: e.target.checked })}
                              style={{ marginRight: '6px' }}
                            />
                            Custom Address/Footer
                          </label>
                          {customPreviewTexts.customAddressEnabled && (
                            <textarea
                              value={customPreviewTexts.customAddressText}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, customAddressText: e.target.value })}
                              placeholder="Fabriksparken 19-21, 1.\n2600 Glostrup\nCVR-nr. 65864010"
                              style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '60px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          )}
                          <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Additional address or footer info. Supports {'{{variables}}'}</small>
                        </div>
                        
                        {/* Suffix Text */}
                        <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                            <input
                              type="checkbox"
                              checked={customPreviewTexts.suffixEnabled}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, suffixEnabled: e.target.checked })}
                              style={{ marginRight: '6px' }}
                            />
                            Suffix Text (e.g., additional info)
                          </label>
                          {customPreviewTexts.suffixEnabled && (
                            <textarea
                              value={customPreviewTexts.suffixText}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, suffixText: e.target.value })}
                              placeholder="Additional information..."
                              style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '50px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          )}
                          <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Appears after signature. Supports {'{{variables}}'}</small>
                        </div>
                        
                        {/* Disclaimer */}
                        <div style={{ marginBottom: '6px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontWeight: '600' }}>
                            <input
                              type="checkbox"
                              checked={customPreviewTexts.disclaimerEnabled}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, disclaimerEnabled: e.target.checked })}
                              style={{ marginRight: '6px' }}
                            />
                            Disclaimer Text
                          </label>
                          {customPreviewTexts.disclaimerEnabled && (
                            <textarea
                              value={customPreviewTexts.disclaimerText}
                              onChange={(e) => setCustomPreviewTexts({ ...customPreviewTexts, disclaimerText: e.target.value })}
                              placeholder="Legal disclaimer or confidentiality notice..."
                              style={{ width: '100%', padding: '6px', fontSize: '12px', fontFamily: 'inherit', minHeight: '60px', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          )}
                          <small style={{ color: '#666', fontSize: '10px', display: 'block', marginTop: '4px' }}>Small text at bottom. Supports {'{{variables}}'}</small>
                        </div>
                      </div>
                    </details>
                </div>
              </div>
            )}
          </form>
        </div>
      )}
    </div>

    {/* Saving Modal */}
    {isSaving && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px 60px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#0066cc',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px',
          }} />
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
          }}>
            Saving template...
          </div>
        </div>
      </div>
    )}

    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </>
  );
}

